import { Identify } from '@amplitude/analytics-browser';
import { LogLevel } from '@amplitude/analytics-types';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { useApplication } from '@bonusly/application';

import { ProductTier } from '../who-am-i/lib/company';
import { useIdentityStore } from '../who-am-i/lib/store';

interface Company {
	id: string;
	is_superadmin_initialized: boolean;
	status: string;
	product_tier: string;
}

interface User {
	id: string;
	created_at: string;
	email: string;
	admin: boolean;
	is_manager: boolean;
	locale: string;
	user_mode: string;
	custom_properties: Record<string, any>;
}

interface InitializeConfig {
	company: Company;
	user: User;
}

export const initializeAnalytics = ({ company, user }: InitializeConfig) => {
	const { analyticsClient: client, analyticsAPIKey, analyticsServerURL } = useIdentityStore.getState();
	const env = useApplication.getState().application.env;

	if (client.getUserId() || !analyticsAPIKey) {
		return;
	}

	// session replay for Amplitude during in trial only for engage
	const shouldEnableSessionReplay =
		env !== 'development' && company.status === 'in_trial' && company.product_tier === ProductTier.TierAlpha && user.email?.match(/@bonus\.ly$/) === null;

	if (shouldEnableSessionReplay) {
		const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });
		client.add(sessionReplayTracking);
	}

	client.init(analyticsAPIKey, user.id, {
		logLevel: LogLevel.Error,
		autocapture: false,
		...(!!analyticsServerURL && {
			serverUrl: analyticsServerURL,
		}),
	});

	client.setGroup('companyId', company.id);

	// Add group properties for company
	const groupIdentity = new Identify();
	groupIdentity
		.set('companyStatus', company.status)
		.set('subscriptionPlan', company.product_tier)
		.set('isSuperadminInitialized', company.is_superadmin_initialized);
	client.groupIdentify('companyId', company.id, groupIdentity);

	// Add properties for user
	const identity = new Identify();
	identity
		.set('created_at', new Date(user.created_at).getTime())
		.set('email', user.email)
		.set('is_admin', user.admin)
		.set('is_manager', user.is_manager)
		.set('translation_setting', user.locale)
		.set('user_mode', user.user_mode);

	for (const prop of ['department', 'role']) {
		const value = user.custom_properties[prop];

		if (value) {
			identity.set(prop, value);
		}
	}

	client.identify(identity);
};

export const track = (event: string, data?: any) => {
	const client = useIdentityStore.getState().analyticsClient;
	const application = useApplication.getState().application;

	if (application.isDevelopment()) {
		console.info(`Tracking event: ${event}, data: ${JSON.stringify(data)}`);
	}

	return client?.track(event, data);
};
